import { LEADS_ROUTES, leadDetailPath } from '../constants/routes';
import { LEAD_NOTIFY_KIND as K, LEAD_SEGMENT } from '../constants/enums';
import { STATUS_BY_ID } from '../../ui/tones';

/**
 * Lead alert wording shared by the toast (CommsNotifyProvider) and the bell
 * (NotificationBell). Payloads come from the user-profile lead outbox via the
 * agents feed: `{lead_id, lead_name, kind, actor_name?, from_name?, to_name?,
 * status?, campaign?, link?, count?}`. Text never includes ids, phone or email.
 */

/** Lead event types on the personal broadcast channel and in the bell feed. */
export const LEAD_EVENT = Object.freeze({
  ASSIGNED: 'LEAD_ASSIGNED',
  REASSIGNED: 'LEAD_REASSIGNED',
  STATUS: 'LEAD_STATUS',
  ACTIVITY: 'LEAD_ACTIVITY',
  OUTCOME: 'LEAD_OUTCOME',
  CALLBACK: 'LEAD_CALLBACK',
});

const LEAD_EVENT_TYPES = new Set(Object.values(LEAD_EVENT));

/** `kind` values the wording branches on. */
const KIND = K;

/** Journey outcome that is not a LeadWorkState status. */
const PAID_LABEL = 'Paid';
const BODY_OPEN_LEAD = 'Open the lead';
const BODY_OPEN_MINE = 'Open Leads › Mine';
const MINE_PATH = `${LEADS_ROUTES.LIST}?segment=${LEAD_SEGMENT.MINE}`;

/**
 * @param {string|null|undefined} eventType
 * @returns {boolean} Whether this is one of the lead alert types.
 */
export function isLeadEventType(eventType) {
  return LEAD_EVENT_TYPES.has(eventType);
}

const clean = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const statusLabel = (status) => {
  if (status === KIND.PAID) return PAID_LABEL;
  return STATUS_BY_ID[status]?.label || null;
};

/** The lead's name, or "a lead" / "A lead" at the start of a sentence. */
const leadName = (payload, atStart = false) => clean(payload.lead_name) || (atStart ? 'A lead' : 'a lead');
const possessive = (name) => `${name}'s`;
const teammate = (name, atStart = false) => clean(name) || (atStart ? 'A teammate' : 'a teammate');

function assignedText(p) {
  if (p.kind === KIND.GROUPED) {
    const count = Number(p.count);
    const title = Number.isInteger(count) && count > 0 ? `${count} new leads for you` : 'New leads for you';
    return { title, body: BODY_OPEN_MINE };
  }
  const campaign = clean(p.campaign);
  const title = `New lead for you: ${leadName(p)}${campaign ? ` · ${campaign}` : ''}`;
  let body = BODY_OPEN_LEAD;
  if (p.kind === KIND.MANUAL && clean(p.actor_name)) body = `Assigned by ${clean(p.actor_name)}`;
  if (p.kind === KIND.RETURNING) body = 'They came back with a new form';
  if ((p.kind === KIND.HANDOVER || p.kind === KIND.ON_LEAVE) && clean(p.from_name)) body = `Passed on while ${clean(p.from_name)} is on leave`;
  return { title, body };
}

function reassignedText(p) {
  if (p.kind === KIND.TAKE_OVER) {
    return { title: `${teammate(p.actor_name || p.to_name, true)} took over ${leadName(p)}`, body: BODY_OPEN_LEAD };
  }
  const passed = `${leadName(p, true)} was passed to ${teammate(p.to_name)}`;
  const title = p.kind === KIND.ON_LEAVE ? `${passed} while you're on leave` : passed;
  return { title, body: BODY_OPEN_LEAD };
}

function statusText(p) {
  const actor = teammate(p.actor_name, true);
  const label = statusLabel(p.status);
  const title = label ? `${actor} marked ${leadName(p)} as ${label}` : `${actor} updated ${possessive(leadName(p))} status`;
  return { title, body: BODY_OPEN_LEAD };
}

function activityText(p) {
  const name = leadName(p, true);
  const titles = {
    [KIND.PAID]: `${name} paid`,
    [KIND.LAPSED]: `${possessive(name)} membership ended · back to New`,
    [KIND.REPEAT_FILL]: `${name} filled another form`,
  };
  return { title: titles[p.kind] || `${name} has an update`, body: BODY_OPEN_LEAD };
}

function outcomeText(p) {
  const actor = clean(p.actor_name);
  // the backend sends the outcome as kind (consult_booked | not_interested | paid)
  const lead = `${leadName(p, !actor)} · ${statusLabel(p.status || p.kind) || 'Updated'}`;
  return { title: actor ? `${actor}: ${lead}` : lead, body: BODY_OPEN_LEAD };
}

const TEXT_BY_TYPE = Object.freeze({
  [LEAD_EVENT.ASSIGNED]: assignedText,
  [LEAD_EVENT.REASSIGNED]: reassignedText,
  [LEAD_EVENT.STATUS]: statusText,
  [LEAD_EVENT.ACTIVITY]: activityText,
  [LEAD_EVENT.OUTCOME]: outcomeText,
  [LEAD_EVENT.CALLBACK]: (p) => ({ title: `Call ${leadName(p)} back now`, body: 'Callback due' }),
});

/**
 * Toast / bell text for a lead alert.
 * @param {string} eventType - One of LEAD_EVENT.
 * @param {Object} [payload]
 * @returns {{ title: string, body: string }}
 */
export function leadAlertText(eventType, payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const build = TEXT_BY_TYPE[eventType];
  return build ? build(p) : { title: 'Lead update', body: 'Open Leads' };
}

/**
 * Where a lead alert opens: the lead page, or Leads › Mine for a grouped
 * assignment (or the Leads list when no lead id came with it).
 * @param {string} eventType
 * @param {Object} [payload]
 * @returns {string}
 */
export function leadAlertTarget(eventType, payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  if (eventType === LEAD_EVENT.ASSIGNED && p.kind === KIND.GROUPED) return MINE_PATH;
  const leadId = clean(typeof p.lead_id === 'number' ? String(p.lead_id) : p.lead_id);
  return leadId ? leadDetailPath(leadId) : LEADS_ROUTES.LIST;
}
